import React, { useRef, useMemo } from "react";
import { graphql, Link, useStaticQuery } from "gatsby";
import * as c from "./footer.module.scss";
import classNames from "classnames";
import StaticGradient from "../../shared/staticGradient";
import mangomintLogo from "@images/footer/mangomint-logo.svg";
import FooterTagline from "./footerTagline";
import FooterWave from "./footerWave";
import { useWindowSize } from "@hooks/useWindowSize";

const businessTypesOrderDesktop = [
  "hair-salon-software",
  "medical-spa-software",
  "iv-therapy-software",
  "skincare-studio-software",
  "beauty-studio-software",
  "massage-studio-software",
  "hair-removal-studio-software",
  "tattoo-piercing-studio-software",
  "nail-salon-software",
  "barbershop-software",
  "wellness-center-software",
  "spa-software",
];

const businessTypesOrderMobile = [
  "hair-salon-software",
  "spa-software",
  "nail-salon-software",
  "medical-spa-software",
  "iv-therapy-software",
  "beauty-studio-software",
  "barbershop-software",
  "massage-studio-software",
  "tattoo-piercing-studio-software",
  "wellness-center-software",
  "hair-removal-studio-software",
  "skincare-studio-software",
];

const featuresOrderDesktop = [
  "scheduling",
  "automated-flows",
  "payments",
  "campaigns",
  "online-booking",
  "offers-and-discounts",
  "express-booking",
  "virtual-waiting-room",
  "mobile-apps",
  "retail-and-inventory",
  "client-management",
  "staff-management",
  "two-way-texting",
  "reporting",
  "memberships-and-packages",
  "multi-location",
  "integrated-forms",
  "payroll-processing",
  "gift-cards",
  "integrations",
];

const featuresOrderMobile = [
  "scheduling",
  "payments",
  "online-booking",
  "express-booking",
  "mobile-apps",
  "client-management",
  "two-way-texting",
  "memberships-and-packages",
  "integrated-forms",
  "gift-cards",
  "automated-flows",
  "campaigns",
  "offers-and-discounts",
  "virtual-waiting-room",
  "retail-and-inventory",
  "staff-management",
  "reporting",
  "multi-location",
  "payroll-processing",
  "integrations",
];

const getFeatureUrl = (slug) => {
  if (slug === "payments") {
    return "/payments/";
  }
  if (slug === "integrations") {
    return "/integrations/";
  }
  return `/features/${slug}/`;
};

const FooterSection = React.memo(({ section, className }) => {
  return (
    <div className={c.category}>
      <div className={className}>
        <p className={classNames(c.categoryTitle, "eyebrow")}>{section.heading}</p>
        <ul>
          {/* For sections with links (platform, resources, social) */}
          {section.links &&
            section.links.map((link) => (
              <li className={"p4"} key={link.title}>
                {link.external || section.heading === "FOLLOW US" ? (
                  <a href={link.url} target="_blank" rel="noreferrer">
                    {link.title}
                  </a>
                ) : (
                  <Link to={link.url}>{link.title}</Link>
                )}
              </li>
            ))}

          {/* For sections with items (business types, features) */}
          {section.items &&
            section.items.map((item) => {
              const url = section.heading === "BUSINESS TYPES" ? `/solutions/${item.slug}` : getFeatureUrl(item.slug);

              return (
                <li className={classNames("p4", c.capitalize)} key={item.title}>
                  <Link to={url}>{item.title}</Link>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
});

const FooterLegal = React.memo(({ legal }) => {
  return (
    <div className={c.legal}>
      <div>{legal.copyright}</div>
      <div className={c.legalLinks}>
        {legal.links.map((link) => (
          <Link to={link.url} key={link.title}>
            {link.title}
          </Link>
        ))}
      </div>
    </div>
  );
});

export default function Footer({ className, includeGradient }) {
  const { isBelowBreakpoint } = useWindowSize("md");

  const data = useStaticQuery(
    graphql`
      query {
        features: allContentfulLandingPage(
          filter: {
            pageVariant: { in: ["Showcase Page", "Feature Page"] }
            slug: { nin: ["marketing-and-automation"] }
          }
        ) {
          nodes {
            slug
            title: navbarOverviewTitle
          }
        }
        businessTypes: allContentfulLandingPage(
          filter: {
            pageVariant: { in: ["Business Page"] }
            navbarOverviewTitle: { ne: null }
            slug: { nin: ["example-business-page", "salon-management-software"] }
          }
        ) {
          nodes {
            slug
            title: navbarOverviewTitle
          }
        }
      }
    `
  );

  const footerData = useMemo(() => {
    const businessTypesMap = {};
    data?.businessTypes?.nodes.forEach((item) => {
      businessTypesMap[item.slug] = {
        title: item.title,
        slug: item.slug,
      };
    });

    const featuresMap = {};
    data?.features?.nodes.forEach((item) => {
      featuresMap[item.slug] = {
        title: item.title,
        slug: item.slug,
      };
    });

    if (!featuresMap["integrations"]) {
      featuresMap["integrations"] = {
        title: "Integrations",
        slug: "integrations",
      };
    }

    const businessTypesOrder = isBelowBreakpoint ? businessTypesOrderMobile : businessTypesOrderDesktop;
    const businessTypes = businessTypesOrder.map((slug) => businessTypesMap[slug]).filter(Boolean);

    const featuresOrder = isBelowBreakpoint ? featuresOrderMobile : featuresOrderDesktop;
    const features = featuresOrder.map((slug) => featuresMap[slug]).filter(Boolean);

    return {
      platform: {
        heading: "PLATFORM",
        links: [
          { title: "Why We're Different", url: "/why-mangomint/" },
          { title: "Pricing", url: "/pricing/" },
          { title: "Watch a video tour", url: "/tour/" },
          { title: "Book a live demo", url: "/demo/" },
          { title: "Try it now", url: "/trial/" },
          { title: "Log in", url: "https://app.mangomint.com/", external: true },
        ],
      },
      businessTypes: {
        heading: "BUSINESS TYPES",
        items: businessTypes,
      },
      features: {
        heading: "FEATURES",
        items: features,
      },
      resources: {
        heading: "RESOURCES",
        links: [
          { title: "Blog", url: "/blog/" },
          { title: "Video Library", url: "/videos/" },
          { title: "Learning Center", url: "/learn/" },
          { title: "About Us", url: "/about-us/" },
          { title: "Careers", url: "/careers/" },
          { title: "Contact Us", url: "/contact-us/" },
        ],
      },
      social: {
        heading: "FOLLOW US",
        links: [
          { title: "LinkedIn", url: "https://www.linkedin.com/company/mangomint" },
          { title: "YouTube", url: "https://www.youtube.com/@mangomint" },
        ],
      },
      legal: {
        copyright: "© Mangomint. Inc. All rights reserved.",
        links: [
          { title: "Legal", url: "/legal/privacy-policy/" },
          { title: "Terms of Service", url: "/legal/website-terms-of-use/" },
          { title: "Privacy Policy", url: "/legal/privacy-policy/" },
        ],
      },
    };
  }, [data, isBelowBreakpoint]);

  const footerContainer = useRef();

  return (
    <>
      <footer id="mt-footer" className={classNames(className, c.footer)} ref={footerContainer} data-transition="footer">
        <div className={c.footerBg}>
          {includeGradient && (
            <StaticGradient
              centerGradient={true}
              opacity="0.45"
              top="-250px"
              mask="linear-gradient(to bottom, transparent, #000 200px, #000 calc(80% - 200px), transparent)"
              height="calc(100% + 200px)"
              gradientWidth="100%"
            />
          )}
        </div>

        <div className={c.footerContent}>
          <div className={c.wrapper}>
            <Link to="/" className={c.logo}>
              <img src={mangomintLogo} height="27" width="180" alt="Mangomint Logo - Footer" />
            </Link>
            <FooterSection section={footerData.platform} className={c.platform} />
            <FooterSection section={footerData.businessTypes} className={c.businessTypes} />
            <FooterSection section={footerData.resources} className={c.resources} />
            <FooterSection section={footerData.features} className={c.features} />
            <FooterSection section={footerData.social} className={c.social} />
          </div>

          <FooterWave />

          <FooterLegal legal={footerData.legal} />
        </div>
      </footer>
      <FooterTagline className={className} />
    </>
  );
}
